'use client';
import { resolveAskInputSubmitButtonPolicy } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/submission-policy/submitButtonPolicy.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useGateSearchModelSelection } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useGateSearchModelSelection.js';
import { usePreferredSearchModelForSearchMode } from '#pplx/frontend/apps/ask/search/hooks/usePreferredSearchModelForSearchMode.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useMemo } from 'react';
const MIN_COUNCIL_MODEL_COUNT = 2;
function needsMoreCouncilModels(selectedSearchMode, compareModelCount) {
    return (selectedSearchMode === SearchMode.AGENTIC_RESEARCH &&
        compareModelCount < MIN_COUNCIL_MODEL_COUNT);
}
/** Resolves input eligibility and button-only analytics without inspecting draft text. */
export function resolveComposerSubmitPolicy({ compareModelCount, gateSearchModeSelection, getPreferredSearchModelForSearchMode, hasAttachments, isFollowUp = false, querySource, selectedSearchMode, showStopButton, skipSearchModeGate = false, submission, }) {
    if (needsMoreCouncilModels(selectedSearchMode, compareModelCount)) {
        return { accepted: false, kind: 'insufficient-models' };
    }
    if (submission.keyEvent !== undefined) {
        return { accepted: true, kind: 'keyboard' };
    }
    return {
        ...resolveAskInputSubmitButtonPolicy({
            gateSearchModeSelection,
            getPreferredSearchModelForSearchMode,
            hasAttachments,
            isFollowUp,
            querySource,
            selectedSearchMode,
            showStopButton,
            skipSearchModeGate,
        }),
        kind: 'pointer',
    };
}
/** Adapts legacy Model Council eligibility and submit-button policy to Composer. */
export function useComposerSubmitControls({ compareModelCount, hasAttachments, isFollowUp = false, querySource, selectedSearchMode, showStopButton, skipSearchModeGate = false, }) {
    const { track } = useInstrumentation();
    const openPaywallUpsell = usePaywallUpsell();
    const { gateSearchModeSelection } = useGateSearchModelSelection({ openPaywallUpsell });
    const { getPreferredSearchModelForSearchMode } = usePreferredSearchModelForSearchMode();
    const isModelCouncilSubmitDisabled = needsMoreCouncilModels(selectedSearchMode, compareModelCount);
    const onBeforeComposerSubmit = useCallback((submission) => {
        const decision = resolveComposerSubmitPolicy({
            compareModelCount,
            gateSearchModeSelection,
            getPreferredSearchModelForSearchMode,
            hasAttachments,
            isFollowUp,
            querySource,
            selectedSearchMode,
            showStopButton,
            skipSearchModeGate,
            submission,
        });
        switch (decision.kind) {
            case 'insufficient-models':
                return false;
            case 'pointer':
                track(decision.analyticsEvent);
                return decision.accepted;
            case 'keyboard':
                return decision.accepted;
            default:
                return assertUnreachableValue(decision);
        }
    }, [
        compareModelCount,
        gateSearchModeSelection,
        getPreferredSearchModelForSearchMode,
        hasAttachments,
        isFollowUp,
        querySource,
        selectedSearchMode,
        showStopButton,
        skipSearchModeGate,
        track,
    ]);
    return useMemo(() => ({ isModelCouncilSubmitDisabled, onBeforeComposerSubmit }), [isModelCouncilSubmitDisabled, onBeforeComposerSubmit]);
}
//# sourceMappingURL=useComposerSubmitControls.js.map